import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { isStrongPassword } from '@/lib/accountValidation';
import { authService, patientService, normalizeImageValue } from '@/services/api';
import { 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Calendar, 
  FileText, 
  Settings,
  LogOut,
  ChevronRight,
  Edit2,
  Loader2,
  Camera,
  Trash2
} from 'lucide-react';
const buildFullName = (firstName, lastName) =>
  [firstName, lastName]
    .map((value) => (value || '').toString().trim())
    .filter(Boolean)
    .join(' ');

const splitFullName = (fullName = '') => {
  const parts = String(fullName || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean);

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

const resolveProfileData = (response) => {
  const payload = response?.data?.data || response?.data || {};
  return payload?.profile || payload?.user || payload;
};

const resolveUserName = (user) =>
  buildFullName(user?.first_name, user?.last_name) ||
  user?.name ||
  '';

const resolvePhoneNumber = (profile) =>
  profile?.phone_number ||
  profile?.phone ||
  profile?.mobile ||
  '';

const resolveProfileImage = (profile) => {
  const media = Array.isArray(profile?.media) ? profile.media[0] : profile?.media;

  return normalizeImageValue(
    profile?.image ||
    profile?.avatar ||
    profile?.profile_image ||
    profile?.profilePicture ||
    profile?.photo ||
    media?.url ||
    media?.thumb ||
    ''
  );
};

const withImageCacheVersion = (imageUrl, version) => {
  const normalizedImageUrl = normalizeImageValue(imageUrl);
  if (
    !normalizedImageUrl ||
    !version ||
    normalizedImageUrl.startsWith('data:image') ||
    normalizedImageUrl.startsWith('blob:')
  ) {
    return normalizedImageUrl;
  }

  const separator = normalizedImageUrl.includes('?') ? '&' : '?';
  return `${normalizedImageUrl}${separator}v=${version}`;
};

const normalizePhone = (value) => {
  let phone = String(value || '').trim().replace(/\s+/g, '').replace(/-/g, '');
  if (phone.startsWith('+9630')) {
    phone = `+963${phone.slice(5)}`;
  }

  return phone;
};

const extractVerificationToken = (response) =>
  response?.data?.data?.verification_token ||
  response?.data?.verification_token ||
  '';

const extractResponseMessage = (response, fallback) =>
  response?.data?.data?.message ||
  response?.data?.message ||
  response?.data?.error ||
  fallback;

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.data?.message ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAuthenticated, loading: authLoading, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profileImageInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [phoneVerificationStep, setPhoneVerificationStep] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
  });
  const [profileImage, setProfileImage] = useState('');
  const [profileImageVersion, setProfileImageVersion] = useState(0);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const isArabic = (i18n.language || '').toLowerCase().startsWith('ar');
  const deleteAccountCopy = isArabic
    ? {
        button: 'حذف الحساب',
        title: 'حذف الحساب',
        description: 'هل أنت متأكد أنك تريد حذف حسابك',
        cancel: 'إلغاء',
        confirm: 'حذف الحساب',
        deleting: 'جارٍ الحذف...',
        deletedSuccess: 'تم حذف الحساب',
        error: 'فشل في حذف الحساب',
      }
    : {
        button: 'Delete Account',
        title: 'Delete Account',
        description: 'Are you sure you want to delete your account',
        cancel: 'Cancel',
        confirm: 'Delete Account',
        deleting: 'Deleting...',
        deletedSuccess: 'Account deleted successfully',
        error: 'Failed to delete account',
      };

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user?.user_type === 'doctor') {
      navigate('/doctor/profile', { replace: true, state: location.state });
    }
  }, [authLoading, isAuthenticated, location.state, navigate, user?.user_type]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: resolveUserName(user),
        email: user.email || '',
        phone_number: resolvePhoneNumber(user),
      });
      setProfileImage(resolveProfileImage(user));
    }
  }, [user]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || editMode || user?.user_type === 'doctor') {
      return undefined;
    }

    let cancelled = false;

    patientService.getProfile()
      .then((response) => {
        if (cancelled) {
          return;
        }

        const profile = resolveProfileData(response);
        setFormData({
          name:
            buildFullName(profile.first_name, profile.last_name) ||
            profile.name ||
            resolveUserName(user),
          email: profile.email || user?.email || '',
          phone_number: resolvePhoneNumber(profile) || resolvePhoneNumber(user),
        });
        setProfileImage(resolveProfileImage(profile) || resolveProfileImage(user));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [authLoading, editMode, isAuthenticated, user]);

  useEffect(() => {
    if (!location.state?.openChangePassword) {
      return;
    }

    setShowPasswordFields(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const buildImageUpdatePayload = (updates = {}) => {
    const fullName = resolveUserName(user) || formData.name.trim();
    const { firstName, lastName } = splitFullName(fullName);
    const phone = normalizePhone(resolvePhoneNumber(user) || formData.phone_number);
    const email = (user?.email || formData.email || '').trim();
    const payload = {
      name: fullName,
      first_name: user?.first_name || firstName,
      last_name: user?.last_name || lastName,
      partner_type: user?.partner_type || user?.user_type || 'patient',
      ...updates,
    };

    if (email) {
      payload.email = email;
    }

    if (phone) {
      payload.phone = phone;
      payload.phone_number = phone;
    }

    if (profileImage && !updates.remove_image && !updates.removeImage) {
      payload.image = profileImage;
    }

    return payload;
  };

  const syncProfileImage = (updatedProfile, { removed = false } = {}) => {
    const nextImage = removed ? '' : resolveProfileImage(updatedProfile);
    const imageVersion = Date.now();
    setProfileImage(nextImage);
    setProfileImageVersion(imageVersion);
    updateUser({
      ...updatedProfile,
      image: nextImage || null,
      avatar: nextImage || null,
      profile_image_updated_at: imageVersion,
    });
  };

  const updateProfileImage = async (updates, options = {}) => {
    setImageLoading(true);
    try {
      const response = await patientService.updateProfile(buildImageUpdatePayload(updates));
      const updatedProfile = resolveProfileData(response);
      syncProfileImage(updatedProfile, options);
      toast.success(t('common.success'), {
        description: options.removed
          ? 'Profile picture removed successfully'
          : 'Profile picture updated successfully',
      });
    } catch (error) {
      toast.error(t('common.error'), {
        description: error.response?.data?.message || error.message || 'Failed to update profile picture',
      });
    } finally {
      setImageLoading(false);
    }
  };

  const handleChooseProfileImage = () => {
    if (imageLoading) {
      return;
    }
    profileImageInputRef.current?.click();
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type?.startsWith('image/')) {
      toast.error(t('common.error'), {
        description: 'Please choose an image file',
      });
      return;
    }

    await updateProfileImage({ imageFile: file });
  };

  const handleRemoveProfileImage = async (event) => {
    event.stopPropagation();
    if (imageLoading || !profileImage) {
      return;
    }

    await updateProfileImage({ remove_image: true }, { removed: true });
  };

  const saveProfilePayload = async (
    payload,
    { currentPhoneNumber, nextPhoneNumber, phoneChanged, fullName, email }
  ) => {
    const response = await patientService.updateProfile(payload);
    const updatedProfile = resolveProfileData(response);
    const fallbackName = payload.name || fullName;
    const fallbackEmail = payload.email || email;
    const updatedName =
      buildFullName(updatedProfile.first_name, updatedProfile.last_name) ||
      updatedProfile.name ||
      fallbackName;
    const updatedImage = resolveProfileImage(updatedProfile) || profileImage;
    const imageVersion = Date.now();
    const updatedNameParts = splitFullName(updatedName);
    const updatedPhoneNumber =
      resolvePhoneNumber(updatedProfile) ||
      (phoneChanged ? nextPhoneNumber : currentPhoneNumber);

    updateUser({
      ...updatedProfile,
      name: updatedName,
      first_name: updatedNameParts.firstName,
      last_name: updatedNameParts.lastName,
      email: updatedProfile.email || fallbackEmail,
      phone_number: updatedPhoneNumber,
      phone: updatedPhoneNumber,
      image: updatedImage || null,
      avatar: updatedImage || null,
      profile_image_updated_at: imageVersion,
    }, { persistAccountProfile: true });

    setProfileImage(updatedImage);
    setProfileImageVersion(imageVersion);
    setFormData({
      name: updatedName,
      email: updatedProfile.email || fallbackEmail,
      phone_number: updatedPhoneNumber,
    });

    toast.success(t('common.success'), {
      description: 'Profile updated successfully',
    });
    setEditMode(false);
    setPhoneVerificationStep(null);
    setOtpCode('');
    setOtpError('');
  };

  const buildSavePayload = () => {
    const fullName = formData.name.trim();
    const email = formData.email.trim();
    const { firstName, lastName } = splitFullName(fullName);
    const currentPhoneNumber = normalizePhone(resolvePhoneNumber(user));
    const nextPhoneNumber = normalizePhone(formData.phone_number);
    const phoneChanged = nextPhoneNumber !== currentPhoneNumber;
    const payload = {
      name: fullName,
      first_name: firstName,
      last_name: lastName,
      partner_type: user?.partner_type || user?.user_type || 'patient',
    };

    if (email) {
      payload.email = email;
    }

    if (profileImage) {
      payload.image = profileImage;
    }

    if (phoneChanged) {
      payload.phone = nextPhoneNumber;
      payload.phone_number = nextPhoneNumber;
    }

    return {
      payload,
      fullName,
      email,
      currentPhoneNumber,
      nextPhoneNumber,
      phoneChanged,
    };
  };

  const handleSave = async () => {
    const saveData = buildSavePayload();

    if (!saveData.fullName) {
      toast.error(t('common.error'), {
        description: 'Please enter your full name',
      });
      return;
    }

    if (saveData.email && !saveData.email.includes('@')) {
      toast.error(t('common.error'), {
        description: 'Please enter a valid email',
      });
      return;
    }

    setLoading(true);
    try {
      if (saveData.phoneChanged) {
        if (!saveData.nextPhoneNumber) {
          throw new Error('Please enter your phone number');
        }

        await authService.requestPatientPhoneVerification(saveData.nextPhoneNumber);
        setPhoneVerificationStep(saveData);
        setOtpCode('');
        setOtpError('');
        toast.success(t('common.success'), {
          description: 'Verification code sent successfully',
        });
        return;
      }

      await saveProfilePayload(saveData.payload, saveData);
    } catch (error) {
      toast.error(t('common.error'), {
        description: error.response?.data?.message || error.message || 'Failed to update profile',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!phoneVerificationStep) {
      return;
    }

    if (!otpCode.trim()) {
      setOtpError('Please enter the verification code');
      return;
    }

    if (otpCode.trim().length < 4) {
      setOtpError('Please enter a valid verification code');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    try {
      const response = await authService.verifyPatientPhoneVerification(
        phoneVerificationStep.nextPhoneNumber,
        otpCode.trim()
      );
      const verificationToken = extractVerificationToken(response);
      if (!verificationToken) {
        throw new Error('Failed to update profile');
      }

      await saveProfilePayload(
        {
          ...phoneVerificationStep.payload,
          phone_verification_token: verificationToken,
        },
        phoneVerificationStep
      );
      toast.success(t('common.success'), {
        description: 'Phone verified successfully',
      });
    } catch (error) {
      setOtpError(error.response?.data?.message || error.message || 'Failed to update profile');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendPhoneOtp = async () => {
    if (!phoneVerificationStep) {
      return;
    }

    setResendingOtp(true);
    setOtpError('');
    try {
      await authService.requestPatientPhoneVerification(phoneVerificationStep.nextPhoneNumber);
      toast.success(t('common.success'), {
        description: 'Verification code sent successfully',
      });
    } catch (error) {
      setOtpError(error.response?.data?.message || error.message || 'Failed to resend verification code');
    } finally {
      setResendingOtp(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deletingAccount) {
      return;
    }

    setDeletingAccount(true);
    try {
      const response = await patientService.deleteAccount();
      const payload = response?.data?.data || response?.data || {};
      if (response?.data?.success === false || payload?.success === false) {
        throw new Error(extractResponseMessage(response, deleteAccountCopy.error));
      }

      toast.success(deleteAccountCopy.deletedSuccess);
      setDeleteAccountOpen(false);
      await logout();
      navigate('/');
    } catch (error) {
      toast.error(t('common.error'), {
        description: extractErrorMessage(error, deleteAccountCopy.error),
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('common.error'), {
        description: 'Please fill in all password fields',
      });
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error(t('common.error'), {
        description: isArabic
          ? 'لا يمكن أن تكون كلمة المرور الجديدة مطابقة لكلمة المرور الحالية'
          : 'New password must be different from current password',
      });
      return;
    }

    if (!isStrongPassword(passwordData.newPassword)) {
      toast.error(t('common.error'), {
        description: isArabic
          ? 'كلمة المرور يجب أن تتضمن 8 محارف وحرفًا كبيرًا وصغيرًا ورقمًا ورمزًا.'
          : 'Password needs 8 characters with uppercase, lowercase, number, and symbol.',
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('common.error'), {
        description: 'New password and confirmation do not match',
      });
      return;
    }

    setPasswordLoading(true);
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success(t('common.success'), {
        description: 'Password changed successfully',
      });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordFields(false);
    } catch (error) {
      toast.error(t('common.error'), {
        description: error.response?.data?.message || 'Failed to change password',
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const menuItems = [
    { icon: Calendar, label: t('nav.appointments'), to: '/appointments' },
    { icon: FileText, label: t('profile.medicalHistory'), to: '/medical-records' },
    { icon: Settings, label: 'Settings', to: '/settings' },
  ];

  if (authLoading || !isAuthenticated) return null;

  const profileImageSrc = withImageCacheVersion(profileImage, profileImageVersion);
  const profileInitials =
    `${user?.first_name?.charAt(0) || ''}${user?.last_name?.charAt(0) || ''}` ||
    user?.name?.charAt(0) ||
    'U';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="profile-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <div className="relative h-20 w-20 shrink-0">
              <button
                type="button"
                className="relative h-20 w-20 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:opacity-80"
                onClick={handleChooseProfileImage}
                disabled={imageLoading}
                title="Change profile picture"
                aria-label="Change profile picture"
              >
                <Avatar className="h-20 w-20 border-4 border-white/20">
                  <AvatarImage src={profileImageSrc || undefined} className="object-cover" />
                  <AvatarFallback className="bg-teal-800 text-white text-2xl">
                    {profileInitials}
                  </AvatarFallback>
                </Avatar>
                <span className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-teal-500 text-white shadow-sm">
                  {imageLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                </span>
              </button>
              {profileImage && !imageLoading ? (
                <button
                  type="button"
                  className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white shadow-sm hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                  onClick={handleRemoveProfileImage}
                  title="Delete profile picture"
                  aria-label="Delete profile picture"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ) : null}
              <input
                ref={profileImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfileImageChange}
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-teal-100">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick Links */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardContent className="p-2">
                {menuItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.to}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-teal-50">
                        <item.icon className="h-5 w-5 text-teal-600" />
                      </div>
                      <span className="font-medium text-slate-700 dark:text-slate-300">{item.label}</span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </Link>
                ))}
              </CardContent>
            </Card>

            {/* Logout Button */}
            <Button 
              variant="outline" 
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" />
              {t('common.logout')}
            </Button>

            <Button
              type="button"
              variant="destructive"
              className="w-full justify-start"
              onClick={() => setDeleteAccountOpen(true)}
              disabled={deletingAccount}
              data-testid="delete-account-button"
            >
              <Trash2 className="h-5 w-5 mr-3" />
              {deleteAccountCopy.button}
            </Button>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('profile.personalInfo')}</CardTitle>
                  <CardDescription>Manage your personal information</CardDescription>
                </div>
                {!editMode ? (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    {t('common.edit')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!editMode}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!editMode}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">{t('auth.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone_number"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      disabled={!editMode}
                      className="pl-10"
                    />
                  </div>
                </div>

                {editMode && (
                  <div className="pt-4 border-t">
                    <Button
                      type="button"
                      variant="link"
                      className="text-teal-600 p-0"
                      onClick={() => setShowPasswordFields((prev) => !prev)}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      {t('profile.changePassword')}
                    </Button>

                    {showPasswordFields && (
                      <div className="mt-4 space-y-4 rounded-xl border border-slate-700/60 p-4">
                        <div className="space-y-2">
                          <Label htmlFor="currentPassword">Current Password</Label>
                          <Input
                            id="currentPassword"
                            name="currentPassword"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="newPassword">New Password</Label>
                          <Input
                            id="newPassword"
                            name="newPassword"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword">Confirm New Password</Label>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                          />
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="button"
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={handleChangePassword}
                            disabled={passwordLoading}
                          >
                            {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        open={deleteAccountOpen}
        onOpenChange={(open) => {
          if (!deletingAccount) {
            setDeleteAccountOpen(open);
          }
        }}
      >
        <DialogContent
          className="border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-white"
          data-testid="delete-account-dialog"
        >
          <DialogHeader>
            <DialogTitle>{deleteAccountCopy.title}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              {deleteAccountCopy.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteAccountOpen(false)}
              disabled={deletingAccount}
            >
              {deleteAccountCopy.cancel}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deletingAccount}
              data-testid="confirm-delete-account-button"
            >
              {deletingAccount ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {deleteAccountCopy.deleting}
                </>
              ) : (
                deleteAccountCopy.confirm
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!phoneVerificationStep}
        onOpenChange={(open) => {
          if (!open && !otpLoading) {
            setPhoneVerificationStep(null);
            setOtpCode('');
            setOtpError('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify phone number</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Enter the code sent to this phone number.
            </p>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
              {phoneVerificationStep?.nextPhoneNumber}
            </div>
            <div className="space-y-3">
              <Label>OTP Code</Label>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => {
                    setOtpCode(value);
                    if (otpError) {
                      setOtpError('');
                    }
                  }}
                  disabled={otpLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpError ? <p className="text-sm text-red-600">{otpError}</p> : null}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleResendPhoneOtp}
              disabled={otpLoading || resendingOtp}
            >
              {resendingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend'}
            </Button>
            <Button
              type="button"
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleVerifyPhoneOtp}
              disabled={otpLoading}
            >
              {otpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify and save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
